import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Globe, GraduationCap } from 'lucide-react';

import { MediaFrame, SplitReveal, TiltCard } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE, STAGGER } from '@/lib/motion-tokens';
import { Badge, Card, cn } from '@ibas/ui';
import { brand } from '@/config/brand';

type Mode = 'Online' | 'On-site';

interface Programme {
  /** Canonical slug — the card links to /programmes/<slug> (data/programmes.ts). */
  slug: string;
  /** Short programme code — rendered as a small gold eyebrow above the title. */
  code: string;
  /** Programme name — rendered as the font-display card title. */
  title: string;
  /** Single-line factual description (institutional content only). */
  description: string;
  /** Delivery mode, surfaced as a Badge. */
  mode: Mode;
  /** Optional campus/location, shown alongside the mode for on-site programmes. */
  location?: string;
  /** Duration/intake line, shown as lighter muted text under the delivery line. */
  detail: string;
  /**
   * True when the ACBSP programme mark may appear (MBA & DBA only, per
   * compliance) — renders a small ACBSP badge overlay on a white plate.
   */
  acbsp?: boolean;
  /** Site path (under /media) to the card's MediaFrame header image — a real IBAS media-library asset. */
  image: string;
  /** Alt text describing the real photo — not an institutional claim beyond what it shows. */
  alt: string;
  /** CSS object-position for the card image, keeping people/subjects in frame. */
  position?: string;
}

/**
 * The four IBAS programmes from the client's programme overview. Copy is drawn
 * strictly from the institutional brief — no invented specialisations, stats or
 * claims. Images are real IBAS media-library assets.
 */
const PROGRAMMES: readonly Programme[] = [
  {
    slug: 'flexi-mba',
    code: 'Flexi-MBA',
    title: 'Master of Business Administration',
    mode: 'Online',
    detail: 'Flexible intake & duration',
    description: 'Study fully online with rolling monthly intakes.',
    acbsp: true,
    // Clean photo (no burned-in badge) so the ACBSP overlay reads once, not twice.
    image: '/media/images/people/online-class-video-call.webp',
    alt: 'IBAS students taking part in an online class over a video call.',
    position: '50% 55%',
  },
  {
    slug: 'flexi-dba',
    code: 'Flexi-DBA',
    title: 'Doctor of Business Administration',
    mode: 'Online',
    detail: '2–3 years · monthly intakes',
    description: 'An online doctoral route built around applied research.',
    acbsp: true,
    // Clean photo (no burned-in badge) so the ACBSP overlay reads once, not twice.
    image: '/media/images/people/ibas-dba-cohort-group-photo-office.webp',
    alt: 'IBAS DBA cohort together for a group photograph in the office.',
    position: '50% 42%',
  },
  {
    slug: 'masters',
    code: 'MBA',
    title: 'Master of Business Administration',
    mode: 'On-site',
    location: 'Switzerland',
    detail: '1–2 years',
    description: 'On-site master’s study at the Swiss campus.',
    acbsp: true,
    image: '/media/images/campus/ibas-classroom-lecture-session.webp',
    alt: 'Students and an instructor in an IBAS classroom during a lecture with a projected slide.',
    position: '50% 55%',
  },
  {
    slug: 'bba',
    code: 'BBA',
    title: 'Bachelor of Business Administration',
    mode: 'On-site',
    location: 'Switzerland',
    detail: '3–4 years',
    description: 'An undergraduate business degree, studied on-site.',
    acbsp: false,
    image: '/media/images/people/student-group-outdoor-gathering.webp',
    alt: 'Group of IBAS students standing and sitting under a tree on campus grounds.',
    position: '50% 62%',
  },
];

export interface ProgrammeShowcaseProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * ProgrammeShowcase — a one-screen horizontal-scroll gallery of the IBAS
 * programme portfolio. Five equal-size cards sit in a single snap row that
 * bleeds to the viewport edges, so the section reads as one screen with no
 * reserved dead space.
 *
 * Motion intent: the gold accent rule under the header draws in as the user
 * scrolls the section into view (GSAP ScrollTrigger, scrubbed to the scroll
 * position rather than firing once), with the header itself drifting at a
 * slightly different rate for a subtle parallax read. Cards rise and settle
 * in a staggered procession on scroll-in, each topped by a real photograph
 * (`MediaFrame`) so the portfolio reads as visual, not just typographic. On
 * hover/pointer each card leans in 3D toward the cursor via the shared
 * `TiltCard` primitive, with a low gold sheen sweeping across the surface.
 * Under reduced motion (or on touch devices) everything renders static and
 * readable — the reveal, parallax, accent draw and tilt are fully disabled.
 */
export function ProgrammeShowcase({ className, id }: ProgrammeShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const accentRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const root = containerRef.current;
      if (!root) return;

      const cards = gsap.utils.toArray<HTMLElement>('[data-programme-card]', root);

      // Staggered scroll-in reveal (skipped entirely under reduced motion so the
      // static end-state — fully visible cards — stays in place). Explicit
      // fromTo with a visible destination (never a bare `from`) so the reveal
      // can't strand the cards hidden, and invalidateOnRefresh recomputes the
      // trigger start once late-loading card imagery settles the layout.
      if (!reduced) {
        gsap.fromTo(
          cards,
          { autoAlpha: 0, y: 44 },
          {
            autoAlpha: 1,
            y: 0,
            duration: DURATION.slow,
            ease: GSAP_EASE.out,
            stagger: STAGGER.base,
            scrollTrigger: {
              trigger: root,
              start: 'top 80%',
              once: true,
              invalidateOnRefresh: true,
            },
          },
        );
      }

      if (reduced) return;

      // Header parallax — the eyebrow/title/lede drift at a slightly slower
      // rate than the page as the section passes through the viewport, tied
      // continuously to scroll position (scrub) rather than firing once.
      if (headerRef.current) {
        gsap.to(headerRef.current, {
          yPercent: -6,
          ease: 'none',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      // Gold accent rule draws in left-to-right as the header scrolls into
      // view — a scrubbed line-draw, not a fire-once fade.
      if (accentRef.current) {
        gsap.fromTo(
          accentRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: headerRef.current ?? accentRef.current,
              start: 'top 85%',
              end: 'top 40%',
              scrub: true,
            },
          },
        );
      }
    },
    { scope: sectionRef, dependencies: [reduced] },
  );

  return (
    <section
      id={id}
      ref={sectionRef}
      className={cn(
        'flex min-h-[100svh] flex-col justify-center overflow-hidden bg-background py-8 md:py-10',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        <header ref={headerRef} className="mb-5 max-w-2xl md:mb-7">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Our Programmes
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            {brand.campaignLine}
          </SplitReveal>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">{brand.essence}</p>
          <span
            ref={accentRef}
            aria-hidden
            className={cn(
              'mt-5 block h-px w-24 origin-left bg-brand-gold',
              !reduced && 'scale-x-0',
            )}
          />
        </header>
      </div>

      {/* Programme grid — all four cards visible at once (no horizontal scroll),
          within the same centred content column as the header. */}
      <div className="mx-auto w-full max-w-6xl px-6">
        <div
          ref={containerRef}
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PROGRAMMES.map((programme) => {
            const isOnline = programme.mode === 'Online';
            const ModeIcon = isOnline ? Globe : GraduationCap;
            return (
              <Link
                key={programme.code}
                to={`/programmes/${programme.slug}`}
                aria-label={`View the ${programme.title} programme`}
                data-programme-card
                data-cursor
                className="group relative block w-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <TiltCard className="h-full" max={6}>
                  <Card className="relative flex h-full flex-col overflow-hidden border-border p-0 shadow-sm transition-shadow duration-500 group-hover:shadow-xl motion-reduce:transition-none">
                    {/* Gold accent sweep — a low sheen that crosses the surface on hover. */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-10 -translate-x-full bg-gradient-to-r from-transparent via-brand-gold/15 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full motion-reduce:hidden"
                    />
                    {/* Gold hairline that draws in along the top edge on hover. */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 top-0 z-10 h-px origin-left scale-x-0 bg-brand-gold/70 transition-transform duration-500 ease-out group-hover:scale-x-100 motion-reduce:hidden"
                    />

                    <div className="relative shrink-0">
                      <MediaFrame
                        image={programme.image}
                        alt={programme.alt}
                        aspect="aspect-[16/10]"
                        rounded="rounded-none"
                        reveal={false}
                        position={programme.position}
                      />
                      {/* ACBSP programme mark as a clean HTML overlay on a white
                          plate (never burned into the photo) — MBA & DBA only. */}
                      {programme.acbsp && (
                        <div className="pointer-events-none absolute top-3 left-3 z-10 rounded-md bg-white p-1 shadow-md ring-1 ring-black/5">
                          <img
                            src="/media/images/accreditation/acbsp-accredited-program-badge.jpg"
                            alt="ACBSP Accredited Program"
                            loading="lazy"
                            className="h-10 w-auto"
                          />
                        </div>
                      )}
                    </div>

                    <div className="relative flex flex-1 flex-col justify-between gap-4 rounded-b-xl border border-t-0 border-transparent p-5 transition-colors duration-500 group-hover:border-brand-gold/30 motion-reduce:transition-none">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex flex-col gap-2">
                          <Badge
                            variant={isOnline ? 'secondary' : 'default'}
                            className="w-fit font-label tracking-wider uppercase"
                          >
                            <ModeIcon aria-hidden className="size-3.5" />
                            {programme.mode}
                            {programme.location ? ` · ${programme.location}` : ''}
                          </Badge>
                          <p className="font-label text-xs tracking-[0.14em] text-muted-foreground uppercase">
                            {programme.detail}
                          </p>
                        </div>
                        <ArrowUpRight
                          aria-hidden
                          className="size-5 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
                        />
                      </div>

                      <div>
                        <p className="font-label text-xs tracking-[0.18em] text-brand-gold uppercase">
                          {programme.code}
                        </p>
                        <h3 className="mt-1.5 font-display text-2xl leading-tight font-semibold text-foreground">
                          {programme.title}
                        </h3>
                        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                          {programme.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                </TiltCard>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 pt-5">
        <p className="text-sm text-muted-foreground">
          Programme recognition details apply —{' '}
          <Link
            to="/about#accreditations"
            className="rounded-sm underline decoration-brand-gold/50 underline-offset-4 transition-colors hover:text-foreground hover:decoration-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            see accreditations
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
